import { useState, useEffect, useCallback } from "react";
import { db } from "../../../firebase";
import { collection, addDoc, updateDoc, getDocs, query, where, orderBy, doc, Timestamp } from "firebase/firestore";

export const useAppointments = (user) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAppointments = useCallback(async () => {
    if (!user) {
      setAppointments([]);
      return;
    }

    setLoading(true);
    try {
      const appointmentsRef = collection(db, 'appointments');
      const q = query(
        appointmentsRef,
        where('userId', '==', user.uid)
      );
      
      const snapshot = await getDocs(q);
      
      const appts = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dateTime: data.dateTime?.toDate ? data.dateTime.toDate() : new Date(data.dateTime),
            endTime: data.endTime?.toDate ? data.endTime.toDate() : new Date(data.endTime),
            completed: data.completed || false
          };
        })
        .filter(apt => !apt.deleted); // Filter out deleted appointments
      
      setAppointments(appts);
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadAppointments();
  }, [user, loadAppointments]);

  const createAppointment = async (appointmentData) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // Validate required fields
      if (!appointmentData.title || !appointmentData.type || !appointmentData.quadrant) {
        throw new Error('Missing required appointment fields: title, type, quadrant');
      }

      // Convert dates to Firestore Timestamps
      const dateTime = appointmentData.dateTime instanceof Date ? Timestamp.fromDate(appointmentData.dateTime) : appointmentData.dateTime;
      const endTime = appointmentData.endTime instanceof Date ? Timestamp.fromDate(appointmentData.endTime) : appointmentData.endTime;

      const appointmentWithUser = {
        title: appointmentData.title,
        type: appointmentData.type,
        quadrant: appointmentData.quadrant,
        dateTime: dateTime,
        endTime: endTime,
        prospect: appointmentData.prospect || false,
        prospectId: appointmentData.prospectId || null,
        notes: appointmentData.notes || "",
        completed: appointmentData.completed || false,
        userId: user.uid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // If updating existing appointment, update in Firebase
      if (appointmentData.id) {
        const appointmentRef = doc(db, 'appointments', appointmentData.id);
        await updateDoc(appointmentRef, {
          ...appointmentWithUser,
          updatedAt: Timestamp.now()
        });

        setAppointments(prev => prev.map(apt =>
          apt.id === appointmentData.id ? { 
            ...apt, 
            ...appointmentWithUser,
            dateTime: appointmentWithUser.dateTime instanceof Timestamp ? appointmentWithUser.dateTime.toDate() : appointmentWithUser.dateTime,
            endTime: appointmentWithUser.endTime instanceof Timestamp ? appointmentWithUser.endTime.toDate() : appointmentWithUser.endTime
          } : apt
        ));

        return appointmentData.id;
      }

      // Create new appointment
      const docRef = await addDoc(collection(db, 'appointments'), appointmentWithUser);
      
      const newAppointment = {
        id: docRef.id,
        ...appointmentWithUser,
        dateTime: appointmentWithUser.dateTime instanceof Timestamp ? appointmentWithUser.dateTime.toDate() : appointmentWithUser.dateTime,
        endTime: appointmentWithUser.endTime instanceof Timestamp ? appointmentWithUser.endTime.toDate() : appointmentWithUser.endTime
      };

      setAppointments(prev => [...prev, newAppointment]);
      return docRef.id;
    } catch (error) {
      console.error('Error creating/updating appointment:', error);
      throw error;
    }
  };

  // Add this function to toggle completion status
  const toggleAppointmentCompletion = async (appointmentId) => {
    try {
      const appointment = appointments.find(apt => apt.id === appointmentId);
      if (!appointment) {
        throw new Error('Appointment not found');
      }

      const newCompletedStatus = !appointment.completed;
      
      // Update in Firebase
      const appointmentRef = doc(db, 'appointments', appointmentId);
      await updateDoc(appointmentRef, {
        completed: newCompletedStatus
      });

      // Update local state
      setAppointments(prev => prev.map(apt => 
        apt.id === appointmentId ? { ...apt, completed: newCompletedStatus } : apt
      ));

      console.log('Appointment completion toggled:', appointmentId, newCompletedStatus);
      return newCompletedStatus;
    } catch (error) {
      console.error('Error toggling appointment completion:', error);
      throw error;
    }
  };

  const deleteAppointment = async (appointmentId) => {
    try {
      if (!appointmentId) {
        throw new Error('Appointment ID is required');
      }

      // Delete from Firebase
      const appointmentRef = doc(db, 'appointments', appointmentId);
      await updateDoc(appointmentRef, {
        deleted: true,
        deletedAt: Timestamp.now()
      });

      // Remove from local state
      setAppointments(prev => prev.filter(apt => apt.id !== appointmentId));

      console.log('Appointment deleted:', appointmentId);
      return true;
    } catch (error) {
      console.error('Error deleting appointment:', error);
      throw error;
    }
  };

  return {
    appointments,
    loading,
    createAppointment,
    toggleAppointmentCompletion,
    deleteAppointment,
    refreshAppointments: loadAppointments
  };
};